/**
 * Hook to fetch and cache user role from API
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../config/api';

export function useUserRole() {
  const { getToken, isSignedIn } = useAuth();
  const [role, setRole] = useState<'admin' | 'user' | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSignedIn) {
      setRole(null);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchUserRole = async () => {
      try {
        const token = await getToken();

        if (!token) {
          console.error('No Clerk token available');
          if (isMounted) {
            setRole('user');
            setLoading(false);
          }
          return;
        }

        const res = await fetch(getApiUrl('api/v1/users/me'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const errorText = await res.text();
          console.error('Failed to fetch user role:', res.status, errorText);
          if (isMounted) {
            setRole('user'); // Default to user if API fails
            setLoading(false);
          }
          return;
        }

        const data = await res.json();
        console.log('User role data:', data);

        if (isMounted && data.success && data.data) {
          setRole(data.data.role || 'user');
        } else if (isMounted) {
          console.error('Invalid user data response:', data);
          setRole('user');
        }
      } catch (error) {
        console.error('Failed to fetch user role:', error);
        if (isMounted) {
          setRole('user'); // Default to user on error
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserRole();

    return () => {
      isMounted = false;
    };
  }, [isSignedIn, getToken]);

  return {
    role,
    isAdmin: role === 'admin',
    loading,
  };
}
